import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, ArrowRight } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ServiceTestimonials from '../../components/ServiceTestimonials';
import ContactFormDialog from '../../components/ContactFormDialog';
import { Button } from '../../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';

const TrademarkSearch = () => {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const pricingPlans = [
    { name: "Basic Search", price: "$49", features: ["USPTO Database Search", "Direct Hit Analysis", "Basic Conflict Report", "Email Delivery"] },
    { name: "Comprehensive", price: "$199", popular: true, features: ["Everything in Basic", "State Database Search", "Common Law Search", "Detailed Risk Assessment", "Attorney Analysis", "Filing Recommendations"] },
    { name: "Premium", price: "$399", features: ["Everything in Comprehensive", "International Search", "Domain Name Analysis", "Social Media Check", "Full Legal Opinion", "Priority Delivery"] }
  ];

  const process = [
    { step: "01", title: "Submit Your Mark", description: "Provide the brand name, logo, or slogan you want to search. We define the right trademark classes for your business." },
    { step: "02", title: "Database Search", description: "Our team searches USPTO records, state registrations, common law sources, and domain databases for conflicts." },
    { step: "03", title: "Report & Recommendations", description: "Receive a detailed search report with conflict analysis, risk assessment, and clear filing recommendations." }
  ];

  const testimonials = [
    { name: "Michael Chen", role: "Startup Founder", text: "The trademark search found conflicts I never would have discovered. Saved my business from a costly rebrand." },
    { name: "Kevin Brown", role: "Manufacturing Owner", text: "Incredibly detailed search report. They identified issues I hadn't considered and recommended smart adjustments." },
    { name: "Laura White", role: "Brand Manager", text: "Comprehensive search across multiple databases. Gave us confidence to proceed with our trademark filing." },
    { name: "Tom Harris", role: "E-commerce Seller", text: "Fast turnaround on the search report. Clear, actionable recommendations that helped us choose the right brand name." },
    { name: "Diana Ross", role: "Marketing Director", text: "The risk assessment was invaluable for our rebranding project. Professional and thorough analysis." }
  ];

  const faqs = [
    { question: "Why is a trademark search important?", answer: "A search identifies existing trademarks that could conflict with yours, helping you avoid USPTO rejection, costly legal disputes, and rebranding expenses. It's a critical first step before filing." },
    { question: "What databases do you search?", answer: "We search the USPTO federal database, state trademark registrations, common law sources, business name databases, and domain records for a comprehensive conflict analysis." },
    { question: "How long does a search take?", answer: "Basic searches are typically completed within 1-2 business days. Comprehensive and premium searches take 3-5 business days depending on complexity." },
    { question: "Can I proceed to filing after the search?", answer: "Yes. If your search results are clear, we can immediately proceed with your trademark application. Our search packages are designed to transition seamlessly into the filing process." }
  ];

  return (
    <div className="min-h-screen bg-wine-950">
      <Header onOpenContactForm={() => setIsContactFormOpen(true)} />
      
      <section className="pt-24 pb-16 bg-gradient-to-b from-wine-900/50 to-wine-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-flex items-center text-gold-400 hover:text-gold-500 mb-6 text-sm font-medium transition-colors"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Home</Link>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4">Comprehensive Trademark Search</h1>
          <p className="text-lg text-wine-300 max-w-2xl mx-auto">Thorough USPTO database search and conflict analysis before you file your trademark application.</p>
        </div>
      </section>

      <section className="py-20 bg-cream-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="rounded-2xl overflow-hidden h-80 shadow-lg">
              <img src="https://images.unsplash.com/photo-1697301439916-169bd6844842?w=800&h=600&fit=crop" alt="Magnifying glass on legal documents for trademark search" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-wine-500 mb-3">About This Service</p>
              <h2 className="text-3xl font-bold text-wine-950 mb-4">Search Before You File</h2>
              <p className="text-stone-600 leading-relaxed mb-4">A comprehensive trademark search is the foundation of a successful registration. Our experts analyze USPTO databases, state records, and common law sources to identify any existing marks that could conflict with yours.</p>
              <p className="text-stone-600 leading-relaxed mb-6">Each search includes a detailed report with risk assessment and actionable recommendations, giving you clarity and confidence before investing in your trademark application.</p>
              <Button onClick={() => setIsContactFormOpen(true)} className="bg-wine-600 hover:bg-wine-700 text-white transition-colors">Start Search</Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-wine-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Our 3-Step Process</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {process.map((item) => (
              <div key={item.step} className="bg-wine-950 p-8 rounded-xl border border-wine-800/40 hover:border-gold-500/30 hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                <div className="w-10 h-10 bg-wine-600 rounded-lg flex items-center justify-center text-white font-bold text-sm mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-sm text-wine-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-wine-950 text-center mb-12">Search Packages</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan) => (
              <div key={plan.name} className={`relative bg-white p-8 rounded-xl border-2 ${plan.popular ? 'border-wine-600 shadow-xl shadow-wine-600/10' : 'border-stone-200'} hover:-translate-y-1 hover:shadow-xl hover:border-gold-400 transition-all duration-300`}>
                {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><span className="bg-wine-600 text-white px-3 py-1 rounded-full text-xs font-bold">RECOMMENDED</span></div>}
                <h3 className="text-xl font-bold text-wine-950 mb-2">{plan.name}</h3>
                <div className="text-4xl font-bold text-wine-950 mb-6">{plan.price}</div>
                <ul className="space-y-3 mb-8">{plan.features.map((f, i) => (<li key={i} className="flex items-start"><Check className="w-4 h-4 text-wine-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-sm text-stone-600">{f}</span></li>))}</ul>
                <Button onClick={() => setIsContactFormOpen(true)} className={`w-full ${plan.popular ? 'bg-wine-600 hover:bg-wine-700 text-white' : 'bg-stone-100 hover:bg-gold-500 hover:text-wine-950 text-stone-800'} transition-colors`}>Choose {plan.name}</Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-wine-900 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400 mb-3">Take Action</p>
          <h2 className="text-3xl font-bold text-white mb-4">Know Before You File</h2>
          <p className="text-wine-200 mb-8">A thorough trademark search saves time, money, and prevents costly conflicts down the road.</p>
          <Button onClick={() => setIsContactFormOpen(true)} className="bg-gold-500 hover:bg-gold-400 text-wine-950 font-semibold px-8 py-5 text-base transition-colors">Start Search <ArrowRight className="ml-2 w-4 h-4" /></Button>
        </div>
      </section>

      <ServiceTestimonials testimonials={testimonials} serviceName="Trademark Search" />

      <section className="py-20" style={{ background: 'linear-gradient(180deg, #1A0A0E 0%, #0D0507 100%)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400 mb-3 text-center">FAQ</p>
          <h2 className="text-3xl font-bold text-white text-center mb-10">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-wine-900/50 rounded-xl px-6 border border-wine-800/40 hover:border-gold-400/30 transition-colors data-[state=open]:border-gold-400/40">
                <AccordionTrigger className="text-left text-base font-medium text-wine-100 hover:text-gold-400 py-5 [&[data-state=open]]:text-gold-400">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-wine-300 text-sm leading-relaxed pb-5">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <Footer onOpenContactForm={() => setIsContactFormOpen(true)} />
      <ContactFormDialog isOpen={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} />
    </div>
  );
};

export default TrademarkSearch;
